import { css } from '@linaria/core'
import type { Virtualizer } from '@tanstack/react-virtual'
import type { FC } from 'react'
import { VirtualizedGrid } from '#features/VirtualizedGrid'
import { fragments, style } from '#styles/index'
import { ChangeRootRow } from './Rows/ChangeRootRow'
import { ChangeHeaderComponent } from './Rows/ChangeTableHeader'
import type { ChangeTableContext } from './useChangesTable'
import type { estimateChangeTableSize } from './utils'

const comparisonTableClass = css`
  flex: 1;
  position: relative;
  overflow: hidden;
  break-inside: avoid;
  display: flex;
  flex-direction: column;
  gap: 35px;

  > ._empty-result {
    pointer-events: none;
    width: 55%;
    height: 100%;
    display: flex;
    position: absolute;
    justify-content: center;
    align-items: center;

    > ._content {
      padding: 48px;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;

      > ._icon {
        height: 120px;
        width: 120px;
        aspect-ratio: 1;
        color: ${style.themeColors.textDisabled};
        opacity: 0.5;
      }
      > ._title {
        ${fragments.textStyle.body.l.regular}
        color: ${style.themeColors.textDisabled};
        text-align: center;
      }
    }
  }
`

const tableClass = css`
  height: 100%;
  overflow: hidden;
`

const gridClass = css`
  min-height: calc(100%);
  display: grid;

  grid-template-rows: max-content;
  grid-template-columns: max-content  4fr min-content min-content 20px 20px max-content 3fr 4fr 24px max-content;
  grid-auto-rows: minmax(12px, min-content);

  transition: 0.5s ease grid-template-columns;
  // grid-auto-columns: minmax(12px, min-content);
  > ._row {
    display: grid;

    // configuring how many columns are there in the total table, each row can then use that space in subgrids
    grid-column: 1/13;
    // this looks funny, but all rows are placed in the first row, then the style injected translateY() works 
    grid-row: 1/1;
  }

  :where(&, ._row) {
    // inherit grid-template-columns to adhere to the layouting rules of grid
    grid-template-columns: subgrid;
    // not necessarily needed, the row can decide its own height
    // grid-template-rows: min-content;


    // the height is set, such as the measureElement can work
    height: max-content;

    break-inside: avoid;
  }
`

const footerFillerClass = css`
  display: grid;
  z-index: -1;
  grid-column: 1/-1;
  grid-row: 2/2;
  grid-template-columns: subgrid;
  // subtracting: header (56px), footer (72px), page header (72px)
  min-height: calc(100vh - 56px - 72px - 72px);
  background-color: ${style.themeColors.backgroundDefault};
  
  > ._left {
    grid-column: 1/6;
    background-color: ${style.themeColors.backgroundBoxed};
  }
 `

const Footer: FC<{ virtualizer: Virtualizer<HTMLDivElement, Element> }> = ({ virtualizer }) => (
  <div className={footerFillerClass} style={{ height: `${virtualizer.getTotalSize() - 56}px` }}>
    <div className="_left" />
  </div>
)

export const ChangesTable: FC<{ context: ChangeTableContext; headerHeight: number; estimateSize: typeof estimateChangeTableSize }> = ({
  context,
  estimateSize,
  headerHeight,
}) => {
  const {
    displayedEntries,
    virtualizer: { setVirtualizerRef },
  } = context

  return (
    <div className={comparisonTableClass}>
      <VirtualizedGrid
        className={tableClass}
        gridClassName={gridClass}
        displayData={displayedEntries}
        headerHeight={headerHeight}
        headerComponent={ChangeHeaderComponent}
        context={context}
        rowComponent={ChangeRootRow}
        estimateSize={estimateSize}
        setVirtualizerRef={setVirtualizerRef}
        footerComponent={Footer}
        paddingEnd={100}
      />
    </div>
  )
}
